import { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Row, Col, Badge } from "react-bootstrap";
// import { useNavigate } from "react-router-dom";
import { customStyles } from "../../styles/table-style";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import DataTable from "react-data-table-component";
import BASE_URL from "../../../assets/global/url";
import { formatCurrency } from "../../../utils/numberFormatter";

const GET_FORECAST_URL = `${BASE_URL}/fixedasset/v2/getFixedAssetForecast`;

const FixedAssetForecastModal = ({
  show,
  onHide,
  status,
  edit,
  id,
  forecast = [],
  allowZeroDepreciation,
  currencyName,
  // currencyId,
  // selectedFixedAsset = [],
  handlePayForecast,
  handlePassForecast,
  handleUpdateFixedAsset,
  handleSaveFixedAsset,
  handleApproveFixedAsset,
  handleRejectFixedAsset,
  validatedFormCheck,
  refreshTrigger,
}) => {
  // const navigate = useNavigate();

  // --- NEW LOGIC: Distinguish between Viewing vs Editing an Approved Asset ---
  const isApprovedView = status === "Approved" && !edit;
  const debounceTimer = useRef(null);

  // --- Search & Filter State ---
  const [searchText, setSearchText] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");

  // --- SERVER-SIDE PAGINATION (For Approved Status View Mode) ---
  const forecastPagination = useServerPagination("about:blank", 10, {}, 1);

  useEffect(() => {
    if (show && isApprovedView && id) {
      setSearchText("");
      setFilterColumn("all");
      forecastPagination.updateApiUrl(GET_FORECAST_URL);
      forecastPagination.updateParams({
        id,
        sortBy: "date",
        sortDirection: "ASC",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isApprovedView, id]);

  // For refreshing data for paying or passing the forecast
  useEffect(() => {
    if (
      show &&
      isApprovedView &&
      id &&
      forecastPagination.dynamicApiUrl !== "about:blank"
    ) {
      forecastPagination.refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // --- CLIENT-SIDE PAGINATION (For Create/Edit/Pending Status) ---
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(10);
  const [localSortField, setLocalSortField] = useState("date");
  const [localSortDirection, setLocalSortDirection] = useState("asc");

  // Local Search Logic
  const filteredLocalData = forecast.filter((item) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();

    if (filterColumn === "date" || filterColumn === "all") {
      if (item.date.includes(searchLower)) return true;
    }
    if (filterColumn === "amount" || filterColumn === "all") {
      if (item.amount.toString().includes(searchLower)) return true;
    }
    return false;
  });

  // Local Sort Logic
  const sortedLocalData = [...filteredLocalData].sort((a, b) => {
    if (localSortField === "date") {
      return localSortDirection === "asc"
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date);
    }
    if (localSortField === "amount") {
      return localSortDirection === "asc"
        ? a.amount - b.amount
        : b.amount - a.amount;
    }
    return 0;
  });

  const totalLocalItems = sortedLocalData.length;
  const totalLocalPages = Math.ceil(totalLocalItems / localItemsPerPage) || 1;
  const paginatedLocalData = sortedLocalData.slice(
    (localCurrentPage - 1) * localItemsPerPage,
    localCurrentPage * localItemsPerPage,
  );

  const localPaginationProps = {
    data: paginatedLocalData,
    currentPage: localCurrentPage,
    setCurrentPage: setLocalCurrentPage,
    itemsPerPage: localItemsPerPage,
    totalPages: totalLocalPages,
    totalItems: totalLocalItems,
    goToPage: (page) => {
      if (page >= 1 && page <= totalLocalPages) setLocalCurrentPage(page);
    },
    nextPage: () => {
      if (localCurrentPage < totalLocalPages)
        setLocalCurrentPage(localCurrentPage + 1);
    },
    prevPage: () => {
      if (localCurrentPage > 1) setLocalCurrentPage(localCurrentPage - 1);
    },
    firstPage: () => setLocalCurrentPage(1),
    lastPage: () => setLocalCurrentPage(totalLocalPages),
    changeItemsPerPage: (newLimit) => {
      setLocalItemsPerPage(Number(newLimit));
      setLocalCurrentPage(1);
    },
    getPageNumbers: () => {
      const pages = [];
      if (totalLocalPages <= 7) {
        for (let i = 1; i <= totalLocalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        if (localCurrentPage > 3) pages.push("...");
        const start = Math.max(2, localCurrentPage - 1);
        const end = Math.min(totalLocalPages - 1, localCurrentPage + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (localCurrentPage < totalLocalPages - 2) pages.push("...");
        pages.push(totalLocalPages);
      }
      return pages;
    },
  };

  // --- UNIFIED HANDLERS ---
  const activeData = isApprovedView
    ? forecastPagination.data
    : localPaginationProps.data;
  const activePagination = isApprovedView
    ? forecastPagination
    : localPaginationProps;

  const handleSearchChange = (value) => {
    setSearchText(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      if (isApprovedView) {
        forecastPagination.updateParams({
          id,
          searchText: value,
          filterColumn,
          sortBy: "date",
          sortDirection: "ASC",
        });
      } else {
        setLocalCurrentPage(1);
      }
    }, 500);
  };

  const handleFilterColumn = (column) => {
    setFilterColumn(column);
    if (searchText) {
      if (isApprovedView) {
        forecastPagination.updateParams({
          id,
          searchText,
          filterColumn: column,
        });
      } else {
        setLocalCurrentPage(1);
      }
    }
  };

  const handleSort = (column, sortDirection) => {
    if (isApprovedView) {
      forecastPagination.updateParams({
        id,
        searchText,
        filterColumn,
        sortBy: column.sortField,
        sortDirection: sortDirection.toUpperCase(),
      });
    } else {
      setLocalSortField(column.sortField);
      setLocalSortDirection(sortDirection.toLowerCase());
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const mainColWidth = isApprovedView ? "35%" : "50%";

  // --- COLUMNS CONFIGURATION ---
  const columns = [
    {
      name: "Date",
      sortField: "date",
      sortable: true,
      width: mainColWidth,
      cell: (row) => (
        <span className="text-muted fw-semibold">
          {new Date(row.date).toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      name: "Amount",
      sortField: "amount",
      sortable: true,
      width: mainColWidth,
      cell: (row) => <span>{formatCurrency(row.amount, currencyName)}</span>,
    },
  ];

  // Only push Action column if Viewing an Approved record
  if (isApprovedView) {
    columns.push({
      name: "Action",
      cell: (row) => {
        const isActionDisabled =
          // selectedFixedAsset.includes(row) ||
          row.isCreated || row.isPaid || row.isPassed;

        return (
          <Row className="gx-2 w-100 m-0">
            <Col xs={6} className="text-center px-1">
              {row.isPaid ? (
                <Badge className="px-2 py-2 fs-6 bg-success-subtle text-success border border-success-subtle w-100">
                  Depreciated
                </Badge>
              ) : (
                !allowZeroDepreciation && (
                  <Button
                    variant="warning"
                    size="sm"
                    className="w-100"
                    onClick={() => handlePayForecast(row.id)}
                    disabled={isActionDisabled}
                  >
                    Depreciate
                  </Button>
                )
              )}
            </Col>

            <Col xs={6} className="text-center px-1">
              {row.isPassed ? (
                <Badge className="px-2 py-2 fs-6 bg-success-subtle text-success border border-success-subtle w-100">
                  Passed
                </Badge>
              ) : (
                <Button
                  variant="warning"
                  size="sm"
                  className="w-100"
                  onClick={() => handlePassForecast(row.fixed_asset_id, row.id)}
                  disabled={isActionDisabled}
                >
                  Pass
                </Button>
              )}
            </Col>
          </Row>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "30%",
    });
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
      dialogClassName="custom-modal-vw90"
    >
      <Form
        noValidate
        validated={validatedFormCheck}
        onSubmit={edit && id ? handleUpdateFixedAsset : handleSaveFixedAsset}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fixed Asset Forecast</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {allowZeroDepreciation ? (
            <div className="alert alert-info mb-0">
              <i className="fa-solid fa-info-circle me-2"></i>
              This asset has zero depreciation amount. No forecast will be
              generated.
            </div>
          ) : (
            <>
              {/* Search filter */}
              <div className="w-100 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={searchText}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="fa-solid fa-sliders"></i>
                    <span className="visually-hidden">Toggle Dropdown</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <button
                        type="button"
                        className={`dropdown-item ${filterColumn === "all" ? "active" : ""}`}
                        onClick={() => handleFilterColumn("all")}
                      >
                        All
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={`dropdown-item ${filterColumn === "date" ? "active" : ""}`}
                        onClick={() => handleFilterColumn("date")}
                      >
                        Date
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={`dropdown-item ${filterColumn === "amount" ? "active" : ""}`}
                        onClick={() => handleFilterColumn("amount")}
                      >
                        Amount
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Data Table */}
              <div className="data-table-cell-width">
                <DataTable
                  columns={columns}
                  data={activeData}
                  customStyles={customStyles}
                  fixedHeader
                  fixedHeaderScrollHeight="calc(100vh - 22rem)"
                  sortServer={isApprovedView}
                  onSort={handleSort}
                  className="dataTable"
                  defaultSortFieldId="date"
                  defaultSortAsc={true}
                />

                {activePagination.totalItems > 0 ? (
                  <PaginationControls {...activePagination} />
                ) : (
                  <div className="text-center text-muted py-3">
                    No forecast data found.
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!status && (
            <>
              <Button type="button" variant="secondary" onClick={onHide}>
                Close
              </Button>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </>
          )}

          {status === "Pending" && (
            <>
              {edit ? (
                <>
                  <Button type="button" variant="secondary" onClick={onHide}>
                    Close
                  </Button>
                  <Button type="submit" variant="primary">
                    Update
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={handleRejectFixedAsset}
                  >
                    Reject
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    onClick={handleApproveFixedAsset}
                  >
                    Approve
                  </Button>
                </>
              )}
            </>
          )}

          {/* ADDED POST-APPROVAL EDIT FOOTER BUTTONS */}
          {status === "Approved" && edit && (
            <>
              <Button type="button" variant="secondary" onClick={onHide}>
                Close
              </Button>
              <Button type="submit" variant="primary">
                Update
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export { FixedAssetForecastModal };
